import {
  MutationCache,
  QueryClient,
  QueryClientProvider as TanstackQueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { type PropsWithChildren } from "react";

import { IqfAxiosError, defaultErrorHandler } from "iqf-web-ui/api-fetch";
import { errorToast } from "iqf-web-ui/toast";

import { domainErrorMessage } from "udzs-common/utils";

function normalizeError(error: Error) {
  return (error instanceof AggregateError ? error.errors : [error]).filter(
    (e) => e instanceof IqfAxiosError,
  );
}

const handledErrors = new WeakSet<IqfAxiosError>();

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      normalizeError(error).forEach((error) => {
        if (handledErrors.has(error)) {
          return;
        }

        handledErrors.add(error);

        const {
          isZodError,
          message,
          config,
          errorHandler = defaultErrorHandler,
        } = error;

        if (isZodError && !error.errorHandler) {
          console.error(config?.url, message);

          errorToast("Chyba pri validácii údajov zo servera.");
          return;
        }

        console.error(error);

        // customErrorMessage má v defaultErrorHandler přednost, proto sem patří ta konkrétnější
        // hláška; univerzální text u volání zůstane záložkou, když backend nic nepošle.
        errorHandler({
          ...error,
          customErrorMessage:
            domainErrorMessage(error) ?? error.customErrorMessage,
          codeErrorMessage:
            "Chyba pri komunikácii so serverom. Skúste to, prosím, znova.",
        });
      });
    },
  }),
});

export function QueryClientProvider({ children }: PropsWithChildren) {
  return (
    <TanstackQueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools />
    </TanstackQueryClientProvider>
  );
}
